import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as authApi from '../../api/authApi';
import { SIGN_IN_ROLES } from '../../auth/signInRole';
import { UmbLogo } from '../../components/layout/UmbLogo';

const ROLE_REQUIRED_MESSAGE = 'Choose the role you sign in as.';

/**
 * Self-service "Forgot password?". One flow whether the person normally uses
 * email/password or Microsoft: the link that arrives resets only the eVault
 * password. The backend answers identically for any email/role, so the page
 * shows the server's message verbatim and never hints whether an account exists.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sentMessage, setSentMessage] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!role) {
      setError(ROLE_REQUIRED_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.forgotPassword({
        emailId: email.trim(),
        roleName: role,
      });
      setSentMessage(result?.message || 'If an eligible account exists, a reset link has been sent.');
    } catch (err) {
      setError(err.message || 'Could not request a password reset');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-eorig-surface px-4 py-10 font-sans text-eorig-ink [word-spacing:0.06em]">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 shadow-eorig-card sm:p-8">
          <UmbLogo />
          <h1 className="mt-6 text-2xl font-semibold tracking-normal text-eorig-ink">
            Reset your password
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-eorig-muted">
            Enter the email you sign in with and the role you sign in as. If an
            eligible account exists we will email you a link to set a new password.
          </p>

          {sentMessage ? (
            <div
              role="status"
              className="mt-6 rounded-xl border border-eorig-border bg-eorig-surface px-4 py-3 text-sm text-eorig-ink"
            >
              <p>{sentMessage}</p>
              <p className="mt-2 text-xs text-eorig-muted">
                Check your spam or junk folder if you do not see the email. The
                link works once.
              </p>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium text-eorig-ink">Email</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium text-eorig-ink">Sign in as</span>
                <select
                  required
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value);
                    if (error === ROLE_REQUIRED_MESSAGE) setError(null);
                  }}
                  className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
                >
                  <option value="">Select your role</option>
                  {SIGN_IN_ROLES.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {error ? (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-xl bg-umb-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-60"
              >
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          <p className="mt-4 text-center text-xs text-eorig-muted">
            Remembered it?{' '}
            <Link to="/signin" className="font-medium text-eorig-cyan underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
